import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './MonitGPS.css';

const ScrollReveal = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const ZawodnikClimb = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    montaz: '',
    srednicaRury: '',
    nip: '',
    firma: '',
    adres: '',
    miasto: '',
    message: ''
  });
  const [chceFakture, setChceFakture] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        montaz: formData.montaz,
        ...(formData.montaz === 'do-klatki' && {
          srednicaRury: formData.srednicaRury,
        }),
        message: formData.message,
        type: 'wyscig-gorski',
        faktura: chceFakture ? 'tak' : 'nie',
        ...(chceFakture && {
          nip: formData.nip,
          firma: formData.firma,
          adres: formData.adres,
          miasto: formData.miasto,
        })
      };

      const response = await fetch('/api/send_order.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus('error');
        setErrorMessage('Wystąpił błąd podczas wysyłania zamówienia. Prosimy skontaktować się telefonicznie.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Wystąpił problem z połączeniem lub serwerem PHP.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 glass-effect"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <a href="/">
              <img
                src="/images/logominitgps.png"
                alt="MonitGPS Logo"
                className="h-12 w-auto cursor-pointer"
              />
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="/" className="hover:text-orange-400 transition-colors">Strona Główna</a>
            <a href="/zawodnik" className="hover:text-orange-400 transition-colors">← Strefa Zawodnika</a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-orange-400 focus:outline-none">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0a]/95 border-b border-white/10 flex flex-col px-6 py-4 space-y-4 text-sm font-medium backdrop-blur-xl">
            <a href="/" className="hover:text-orange-400">Strona Główna</a>
            <a href="/zawodnik" className="hover:text-orange-400">← Strefa Zawodnika</a>
          </div>
        )}
      </motion.nav>

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 flex items-center justify-center overflow-hidden bg-carbon min-h-[50vh]">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>

        <motion.div
          className="absolute top-1/2 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-red-500/15 rounded-full blur-[80px]"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.h1
            className="font-display font-black text-5xl md:text-7xl leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Zawodnik —{' '}
            <span style={{ background: 'linear-gradient(135deg, #f97316, #ef4444)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Wyścig Górski
            </span>
          </motion.h1>

          <motion.p
            className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Materiały, instrukcje obsługi urządzeń oraz możliwość zamówienia zestawów montażowych dla zawodników wyścigów górskich.
          </motion.p>

        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px opacity-50 scan-line"
          style={{ background: 'linear-gradient(to right, transparent, #f97316, transparent)' }}>
        </div>
      </section>

      {/* PDF Downloads Section */}
      <section id="downloads" className="py-24 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium mb-6" style={{ color: '#f97316' }}>
                Do pobrania
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mb-6">
                Instrukcje i{' '}
                <span style={{ background: 'linear-gradient(135deg, #f97316, #ef4444)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  materiały
                </span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.1}>
              <div className="glass-effect rounded-3xl p-8 hover-lift flex flex-col items-center text-center">
                <div className="text-6xl mb-6">📄</div>
                <h3 className="font-display font-bold text-2xl mb-4">Instrukcja Obsługi Urządzenia</h3>
                <p className="text-gray-400 mb-8 max-w-sm">
                  Szczegółowa instrukcja użytkowania trackerów MonitGPS przeznaczona dla zawodników wyścigów górskich.
                </p>
                <a
                  href="/downloads/Instrukcja_dla_Zawodnika_MonitGPS_Gorskie.pdf"
                  download
                  target="_blank"
                  className="w-full px-6 py-4 bg-white/5 border border-white/20 rounded-xl font-display font-bold text-white transition-all flex items-center justify-center space-x-2 relative z-10"
                  style={{ '--hover-border': '#f97316' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(249,115,22,0.5)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'}
                >
                  <span>Pobierz PDF</span>
                  <span className="text-xl">⬇</span>
                </a>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8 hover-lift flex flex-col items-center text-center">
                <div className="text-6xl mb-6">🔧</div>
                <h3 className="font-display font-bold text-2xl mb-4">Schemat Instalacji</h3>
                <p className="text-gray-400 mb-8 max-w-sm">
                  Instrukcja i schemat poprawnego montażu okablowania oraz mocowań dla urządzeń GPS w pojeździe wyścigowym.
                </p>
                <a
                  href="/downloads/Instrukcja_Instalacji_MonitGPS_Gorskie.pdf"
                  download
                  target="_blank"
                  className="w-full px-6 py-4 bg-white/5 border border-white/20 rounded-xl font-display font-bold text-white transition-all flex items-center justify-center space-x-2 relative z-10"
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(249,115,22,0.5)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'}
                >
                  <span>Pobierz PDF</span>
                  <span className="text-xl">⬇</span>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Order / Registration Section */}
      <section id="order-kit" className="py-24 px-6 relative bg-gradient-to-b from-transparent to-[#050505]">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium mb-6" style={{ color: '#f97316' }}>
                Wyposażenie pojazdu
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mb-6">
                Zamów{' '}
                <span style={{ background: 'linear-gradient(135deg, #f97316, #ef4444)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Zestaw / Wypożyczenie
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Zamów dedykowany zestaw do montażu urządzenia MonitGPS na stałe, lub zgłoś chęć wypożyczenia trackera na wyścig górski.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Kit Details */}
            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8 h-full flex flex-col relative overflow-hidden group">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.05), rgba(239,68,68,0.05))' }}>
                </div>

                <div className="mb-6 relative z-10 flex flex-col space-y-4">
                  <div>
                    <h3 className="font-display font-bold text-2xl mb-2" style={{ color: '#f97316' }}>Cennik dla Zawodnika:</h3>
                    <p className="text-gray-400 text-sm">Zgłoś chęć wypożyczenia urządzenia lub kup na własność zestaw montażowy.</p>
                  </div>
                  <div className="flex flex-row flex-wrap gap-3 w-full">
                    <div className="flex-1 min-w-[140px] px-4 py-3 rounded-xl text-center"
                      style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>
                      <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-bold">Wypożyczenie</span>
                      <strong className="text-2xl text-white font-display">150 zł</strong>
                      <span className="ml-1 text-sm font-bold" style={{ color: '#f97316' }}>netto / wyścig</span>
                    </div>
                    <div className="flex-1 min-w-[140px] px-4 py-3 rounded-xl text-center"
                      style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                      <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-bold">Cena Zestawu</span>
                      <strong className="text-2xl text-white font-display">350 zł</strong>
                      <span className="ml-1 text-sm font-bold" style={{ color: '#ef4444' }}>netto / szt.</span>
                    </div>
                  </div>
                </div>

                {/* 3 Images Grid */}
                <div className="grid grid-cols-3 gap-3 mb-8 relative z-10">
                  <div className="aspect-square rounded-xl bg-black/40 border border-white/5 overflow-hidden flex items-center justify-center text-2xl">
                    <img src="/images/kit1.jpg" alt="Zestaw 1" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="absolute z-[-1]">📷</span>
                  </div>
                  <div className="aspect-square rounded-xl bg-black/40 border border-white/5 overflow-hidden flex items-center justify-center text-2xl">
                    <img src="/images/kit2.jpg" alt="Zestaw 2" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="absolute z-[-1]">📷</span>
                  </div>
                  <div className="aspect-square rounded-xl bg-black/40 border border-white/5 overflow-hidden flex items-center justify-center text-2xl">
                    <img src="/images/kit3.jpg" alt="Zestaw 3" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="absolute z-[-1]">📷</span>
                  </div>
                </div>

                {/* Product List */}
                <div className="flex-grow flex flex-col justify-start relative z-10">
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="mr-3 flex-shrink-0" style={{ color: '#f97316' }}>✓</span>
                      <div>
                        <strong className="text-white block text-lg">Antena 2 w 1 (GPS, GPRS)</strong>
                        <span className="text-sm text-gray-400 block mt-1">Długość kabla: 3m</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-3 mt-1 flex-shrink-0" style={{ color: '#f97316' }}>✓</span>
                      <div>
                        <div className="flex items-center flex-wrap gap-3">
                          <strong className="text-white block text-lg">Uchwyty montażowe RAM</strong>
                          <div className="bg-white/10 px-2 py-1 rounded-md flex items-center justify-center">
                            <img src="/images/ram-logo.png" alt="RAM Mounts" className="h-5 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        </div>
                        <div className="text-sm mt-1" style={{ color: 'rgba(249,115,22,0.8)' }}>
                          <span className="font-medium block">Oryginalne elementy mocujące marki RAM:</span>
                          <ul className="list-none mt-2 ml-1 text-gray-300 space-y-2">
                            <li className="flex flex-col">
                              <div className="flex items-start mb-1">
                                <span className="mr-2 text-orange-500">•</span>
                                <span>
                                  <strong>Uchwyt do pojazdu:</strong>
                                </span>
                              </div>
                              <div className="flex flex-col pl-6">
                                <div className="flex items-start">
                                  <span className="mr-2 text-gray-500">-</span>
                                  <span>Uchwyt do karoserii (RAM-B-347U-BT)</span>
                                </div>
                                <div className="my-1 text-orange-400 font-bold">
                                  --- LUB ---
                                </div>
                                <div className="flex items-start">
                                  <span className="mr-2 text-gray-500">-</span>
                                  <span>Uchwyt do rury klatki:</span>
                                </div>
                                <ul className="list-[circle] list-inside pl-6 mt-1 text-gray-300 text-xs space-y-1">
                                  <li>Rozmiar A: 32 - 35 mm</li>
                                  <li>Rozmiar B: 38 - 41 mm</li>
                                  <li>Rozmiar C: 48 - 51 mm</li>
                                </ul>
                              </div>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2 text-orange-500">•</span>
                              <span>
                                <strong>Ramię-łącznik:</strong> RAM-B-201U
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-3 flex-shrink-0" style={{ color: '#f97316' }}>✓</span>
                      <div>
                        <strong className="text-white block text-lg">Kabel zasilający</strong>
                        <span className="text-sm text-gray-400 block mt-1">Długość kabla: 1m</span>
                      </div>
                    </li>
                  </ul>

                  <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 flex flex-col sm:flex-row items-center gap-4">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 bg-black/40 border border-orange-500/30 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {/* Tutaj podmień na swoje zdjęcie */}
                      <img src="/images/led-screen.jpg" alt="Ekran LED" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                      <span className="hidden text-3xl">🚥</span>
                    </div>
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-xl">🚨</span>
                        <strong className="text-orange-400 text-sm tracking-wider uppercase">Wkrótce w ofercie: Ekran LED</strong>
                      </div>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        Wkrótce dostępny będzie wymagany dodatkowy ekran LED montowany przy kierowcy, który poprzez sygnały świetlne powiadomi o zmianie statusu, jak np. RED FLAG.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Order Form */}
            <ScrollReveal delay={0.4}>
              <div className="glass-effect rounded-3xl p-8 relative">
                <h3 className="font-display font-bold text-3xl mb-6 text-white text-center">
                  Formularz zamówienia
                </h3>

                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center text-center py-12 space-y-4">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center text-green-400 text-4xl mb-4">✓</div>
                    <h4 className="text-3xl font-bold font-display text-white">Zamówienie przyjęte!</h4>
                    <p className="text-gray-400 mt-2">Dziękujemy, Twoje zamówienie zostało wysłane. Skontaktujemy się z Tobą wkrótce.</p>
                  </div>
                ) : (
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Imię i nazwisko / Nazwa zespołu</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-1 transition-colors text-white"
                        style={{ '--focus-color': '#f97316' }}
                        onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                        placeholder="Skoda Rally Team"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Adres Email</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white"
                          onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                          placeholder="kontakt@team.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Telefon Kontaktowy</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white"
                          onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                          placeholder="+48 123 456 789"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Typ montażu</label>
                      <select
                        name="montaz"
                        required
                        value={formData.montaz}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white appearance-none cursor-pointer"
                        style={{ background: 'rgba(255,255,255,0.05)' }}
                        onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                      >
                        <option value="" disabled style={{ background: '#1a1a1a' }}>Wybierz typ montażu...</option>
                        <option value="do-tunelu" style={{ background: '#1a1a1a' }}>Do tunelu</option>
                        <option value="do-klatki" style={{ background: '#1a1a1a' }}>Do klatki</option>
                      </select>
                    </div>

                    {formData.montaz === 'do-klatki' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Średnica rury klatki</label>
                        <select
                          name="srednicaRury"
                          required
                          value={formData.srednicaRury || ''}
                          onChange={handleInputChange}
                          className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white appearance-none cursor-pointer"
                          style={{ background: 'rgba(255,255,255,0.05)' }}
                          onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                        >
                          <option value="" disabled style={{ background: '#1a1a1a' }}>Wybierz średnicę...</option>
                          <option value="A - 32-35 mm" style={{ background: '#1a1a1a' }}>A - 32-35 mm</option>
                          <option value="B - 38-41 mm" style={{ background: '#1a1a1a' }}>B - 38 - 41 mm</option>
                          <option value="C - 48-51 mm" style={{ background: '#1a1a1a' }}>C - 48 - 51 mm</option>
                          <option value="D - Inne (podaj w szczegółach)" style={{ background: '#1a1a1a' }}>D - Inne (podaj w szczegółach)</option>
                        </select>
                      </div>
                    )}

                    {/* Faktura question */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-400">Czy potrzebujesz faktury?</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setChceFakture(false)}
                          className="flex-1 py-3 rounded-xl font-display font-bold text-sm transition-all border"
                          style={!chceFakture
                            ? { background: 'rgba(249,115,22,0.15)', borderColor: 'rgba(249,115,22,0.6)', color: '#f97316' }
                            : { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)', color: '#6b7280' }
                          }
                        >
                          Nie
                        </button>
                        <button
                          type="button"
                          onClick={() => setChceFakture(true)}
                          className="flex-1 py-3 rounded-xl font-display font-bold text-sm transition-all border"
                          style={chceFakture
                            ? { background: 'rgba(249,115,22,0.15)', borderColor: 'rgba(249,115,22,0.6)', color: '#f97316' }
                            : { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)', color: '#6b7280' }
                          }
                        >
                          Tak
                        </button>
                      </div>
                    </div>

                    {/* Dane do faktury - visible only when Tak */}
                    {chceFakture && (
                      <div className="space-y-4 p-5 rounded-xl" style={{ background: 'rgba(249,115,22,0.04)', border: '1px solid rgba(249,115,22,0.2)' }}>
                        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(249,115,22,0.7)' }}>🧾 Dane do faktury</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">NIP</label>
                            <input
                              type="text"
                              name="nip"
                              required
                              value={formData.nip}
                              onChange={handleInputChange}
                              className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white"
                              onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                              placeholder="123-456-78-90"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Nazwa firmy</label>
                            <input
                              type="text"
                              name="firma"
                              required
                              value={formData.firma}
                              onChange={handleInputChange}
                              className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white"
                              onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                              placeholder="Racing Team Sp. z o.o."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Adres (ulica i numer)</label>
                          <input
                            type="text"
                            name="adres"
                            required
                            value={formData.adres}
                            onChange={handleInputChange}
                            className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white"
                            onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                            onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                            placeholder="ul. Wyścigowa 1"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Kod pocztowy i miasto</label>
                          <input
                            type="text"
                            name="miasto"
                            required
                            value={formData.miasto}
                            onChange={handleInputChange}
                            className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white"
                            onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                            onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                            placeholder="00-000 Warszawa"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Dodatkowe uwagi / Szczegóły</label>
                      <textarea
                        rows="3"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none transition-colors text-white resize-none"
                        onFocus={e => { e.target.style.borderColor = '#f97316'; e.target.style.boxShadow = '0 0 0 1px #f97316'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                        placeholder="Adres do wysyłki. Ilość sztuk."
                      ></textarea>
                    </div>

                    {errorMessage && (
                      <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-sm font-medium mt-4 mb-4">
                        {errorMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full py-4 mt-4 rounded-xl font-display font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: 'linear-gradient(135deg, #f97316, #ef4444)' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 10px 40px rgba(249,115,22,0.4)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <span>{status === 'loading' ? 'Wysyłanie...' : 'Wyślij Zamówienie'}</span>
                      <span>✉️</span>
                    </button>
                    <p className="text-xs text-center text-gray-500 mt-3">
                      Kliknięcie przycisku wyśle zamówienie do nas. Skontaktujemy się z Tobą w celu potwierdzenia zamówienia.
                    </p>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-6 relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-display font-black text-4xl mb-8">Kontakt z działem technicznym</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2" style={{ color: '#f97316' }}>Maciek</div>
                <a href="tel:504178476" className="text-gray-300 hover:text-orange-400 transition-colors">504 178 476</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2" style={{ color: '#f97316' }}>Adrian</div>
                <a href="tel:791807484" className="text-gray-300 hover:text-orange-400 transition-colors">791 807 484</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2" style={{ color: '#f97316' }}>Dawid</div>
                <a href="tel:603197643" className="text-gray-300 hover:text-orange-400 transition-colors">603 197 643</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 glass-effect border-t border-white/5 mt-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <p>© 2024 MonitGPS. Wszystkie prawa zastrzeżone.</p>
            <a href="/polityka-prywatnosci" className="hover:text-orange-400 transition-colors underline decoration-white/20 underline-offset-4">Polityka Prywatności</a>
          </div>
          <div className="mt-4 md:mt-0">
            <p>Profesjonalny monitoring dla sportów motorowych</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ZawodnikClimb;
